/**
 * @fileoverview Canvas context for managing persistent canvas state
 * @module contexts/CanvasContext
 *
 * The CanvasContext manages state for the right-side canvas panel that
 * displays interactive widgets alongside the chat interface. The canvas
 * is a flexible workspace that can render any type of content:
 *
 * - GTD Tasks (kanban, list, calendar views)
 * - Procurement data (tables, spend analysis)
 * - Account planning (territory cards, opportunity tracking)
 * - Competitive intelligence (comparison matrices, win/loss)
 * - Product research (capability trees, configurations)
 * - Email drafts (composer, templates)
 * - Web search results (scraped data, external sources)
 * - Analytics dashboards (charts, metrics)
 *
 * Widgets persist across messages, can be minimized/expanded, and
 * update in real-time from AI responses or direct manipulation.
 *
 * USAGE:
 * import { useCanvas } from '../contexts/CanvasContext';
 * const { widgets, addWidget, updateWidget, removeWidget } = useCanvas();
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

const CanvasContext = createContext();

// Widget type constants - extensible for any content type
export const WIDGET_TYPES = {
  // Tasks
  TASK_BOARD: 'task_board',
  TASK_LIST: 'task_list',
  TASK_CALENDAR: 'task_calendar',

  // Procurement
  PROCUREMENT_TABLE: 'procurement_table',
  SPEND_ANALYSIS: 'spend_analysis',
  SUPPLIER_CARD: 'supplier_card',
  CLASSIFICATION_QUEUE: 'classification_queue',

  // Account Planning
  ACCOUNT_CARD: 'account_card',
  TERRITORY_MAP: 'territory_map',
  OPPORTUNITY_BOARD: 'opportunity_board',
  ENGAGEMENT_TIMELINE: 'engagement_timeline',

  // Competitive Intel
  COMPARISON_MATRIX: 'comparison_matrix',
  WIN_LOSS_SUMMARY: 'win_loss_summary',
  COMPETITOR_PROFILE: 'competitor_profile',
  DISPLACEMENT_PLAN: 'displacement_plan',

  // Products & Tech Stack
  PRODUCT_GRID: 'product_grid',
  CAPABILITY_TREE: 'capability_tree',
  CONFIG_CARD: 'config_card',
  STACK_DIAGRAM: 'stack_diagram',

  // Framework Analysis
  GAP_ANALYSIS: 'gap_analysis',
  COVERAGE_GAUGE: 'coverage_gauge',
  MATURITY_ASSESSMENT: 'maturity_assessment',

  // Communication
  EMAIL_COMPOSER: 'email_composer',
  MESSAGE_DRAFT: 'message_draft',
  CALL_SCRIPT: 'call_script',

  // Research & Web
  SEARCH_RESULTS: 'search_results',
  DOC_SNIPPETS: 'doc_snippets',
  WEB_CONTENT: 'web_content',
  DATA_TABLE: 'data_table',

  // Analytics
  METRICS_GRID: 'metrics_grid',
  CHART: 'chart',
  REPORT_CARD: 'report_card',
};

// GTD list constants (for task widgets)
export const GTD_LISTS = {
  INBOX: 'inbox',
  NEXT_ACTIONS: 'next_actions',
  WAITING_FOR: 'waiting_for',
  PROJECTS: 'projects',
  SOMEDAY_MAYBE: 'someday_maybe',
  REFERENCE: 'reference',
};

// Context tag constants (for task widgets)
export const CONTEXTS = {
  COMPUTER: '@computer',
  PHONE: '@phone',
  ERRANDS: '@errands',
  HOME: '@home',
  WORK: '@work',
  ANYWHERE: '@anywhere',
  AGENDA: '@agenda',
  READ: '@read',
};

// View modes for task board
export const VIEW_MODES = {
  KANBAN: 'kanban',
  LIST: 'list',
  CALENDAR: 'calendar',
};

// Storage key for persisting canvas state
const CANVAS_STORAGE_KEY = 'ts_canvas_state';

export function CanvasProvider({ children }) {
  // Array of widgets currently in the canvas
  const [widgets, setWidgets] = useState([]);

  // View mode for task-related widgets
  const [taskViewMode, setTaskViewMode] = useState(VIEW_MODES.KANBAN);

  // Active context filter for tasks
  const [activeContextFilter, setActiveContextFilter] = useState(null);

  // Canvas collapsed state (for mobile/responsive)
  const [isCanvasCollapsed, setIsCanvasCollapsed] = useState(false);

  // Animation state for new widgets
  const [animatingWidgets, setAnimatingWidgets] = useState(new Set());

  // Load persisted preferences
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CANVAS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.taskViewMode) setTaskViewMode(parsed.taskViewMode);
        if (parsed.isCanvasCollapsed) setIsCanvasCollapsed(parsed.isCanvasCollapsed);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  // Persist preferences
  useEffect(() => {
    try {
      localStorage.setItem(CANVAS_STORAGE_KEY, JSON.stringify({
        taskViewMode,
        isCanvasCollapsed,
      }));
    } catch (e) {
      // Ignore storage errors
    }
  }, [taskViewMode, isCanvasCollapsed]);

  /**
   * Add a new widget to the canvas
   * @param {object} widget - Widget object with type, data, etc.
   * @returns {string} - Generated widget ID
   */
  const addWidget = useCallback((widget) => {
    const id = widget.id || `widget-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const newWidget = {
      id,
      timestamp: new Date().toISOString(),
      minimized: false,
      pinned: false,
      ...widget,
    };

    // Mark as animating for entrance animation
    setAnimatingWidgets(prev => new Set([...prev, id]));

    // Add to beginning of list (newest first)
    setWidgets(prev => [newWidget, ...prev]);

    // Auto-expand canvas when widget is added
    if (isCanvasCollapsed) {
      setIsCanvasCollapsed(false);
    }

    // Remove from animating set after animation completes
    setTimeout(() => {
      setAnimatingWidgets(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 500);

    return id;
  }, [isCanvasCollapsed]);

  /**
   * Add multiple widgets from AI response
   * @param {array} uiComponents - Array of UI components from AI
   * @param {string} query - The query that generated these widgets
   */
  const addWidgetsFromAI = useCallback((uiComponents, query) => {
    if (!uiComponents || !Array.isArray(uiComponents)) return;

    uiComponents.forEach((component, idx) => {
      // Small delay between widgets for staggered animation
      setTimeout(() => {
        addWidget({
          type: component.type,
          title: component.title,
          data: component.data,
          actions: component.actions,
          query,
          source: 'ai',
        });
      }, idx * 100);
    });
  }, [addWidget]);

  /**
   * Update an existing widget
   * @param {string} widgetId - Widget ID
   * @param {object} updates - Partial updates to merge
   */
  const updateWidget = useCallback((widgetId, updates) => {
    setWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, ...updates, updatedAt: new Date().toISOString() } : w
    ));
  }, []);

  /**
   * Remove a widget from the canvas
   * @param {string} widgetId - Widget ID to remove
   */
  const removeWidget = useCallback((widgetId) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
  }, []);

  /**
   * Toggle widget minimized state
   * @param {string} widgetId - Widget ID
   */
  const toggleWidgetMinimized = useCallback((widgetId) => {
    setWidgets(prev => prev.map(w =>
      w.id === widgetId ? { ...w, minimized: !w.minimized } : w
    ));
  }, []);

  /**
   * Toggle widget pinned state (pinned widgets stay at top)
   * @param {string} widgetId - Widget ID
   */
  const toggleWidgetPinned = useCallback((widgetId) => {
    setWidgets(prev => {
      const updated = prev.map(w =>
        w.id === widgetId ? { ...w, pinned: !w.pinned } : w
      );
      // Sort: pinned first, then by timestamp
      return updated.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
    });
  }, []);

  /**
   * Clear all widgets from the canvas
   */
  const clearCanvas = useCallback(() => {
    setWidgets([]);
    setActiveContextFilter(null);
  }, []);

  /**
   * Toggle canvas collapsed state
   */
  const toggleCanvasCollapsed = useCallback(() => {
    setIsCanvasCollapsed(prev => !prev);
  }, []);

  /**
   * Set the task view mode
   */
  const setTaskView = useCallback((view) => {
    if (Object.values(VIEW_MODES).includes(view)) {
      setTaskViewMode(view);
    }
  }, []);

  /**
   * Set context filter
   */
  const setContextFilter = useCallback((context) => {
    setActiveContextFilter(context);
  }, []);

  /**
   * Check if a widget is animating
   */
  const isWidgetAnimating = useCallback((widgetId) => {
    return animatingWidgets.has(widgetId);
  }, [animatingWidgets]);

  // Compute canvas stats
  const canvasStats = useMemo(() => {
    return {
      totalWidgets: widgets.length,
      pinnedCount: widgets.filter(w => w.pinned).length,
      minimizedCount: widgets.filter(w => w.minimized).length,
      byType: widgets.reduce((acc, w) => {
        acc[w.type] = (acc[w.type] || 0) + 1;
        return acc;
      }, {}),
    };
  }, [widgets]);

  // Get task-specific data if there's a task board widget
  const taskBoardWidget = useMemo(() => {
    return widgets.find(w =>
      w.type === WIDGET_TYPES.TASK_BOARD ||
      w.type === WIDGET_TYPES.TASK_LIST ||
      w.type === WIDGET_TYPES.TASK_CALENDAR
    );
  }, [widgets]);

  const value = useMemo(() => ({
    // State
    widgets,
    taskViewMode,
    activeContextFilter,
    isCanvasCollapsed,
    canvasStats,
    taskBoardWidget,

    // Widget actions
    addWidget,
    addWidgetsFromAI,
    updateWidget,
    removeWidget,
    toggleWidgetMinimized,
    toggleWidgetPinned,
    clearCanvas,
    isWidgetAnimating,

    // UI actions
    toggleCanvasCollapsed,
    setTaskView,
    setContextFilter,

    // Constants
    WIDGET_TYPES,
    GTD_LISTS,
    CONTEXTS,
    VIEW_MODES,
  }), [
    widgets,
    taskViewMode,
    activeContextFilter,
    isCanvasCollapsed,
    canvasStats,
    taskBoardWidget,
    addWidget,
    addWidgetsFromAI,
    updateWidget,
    removeWidget,
    toggleWidgetMinimized,
    toggleWidgetPinned,
    clearCanvas,
    isWidgetAnimating,
    toggleCanvasCollapsed,
    setTaskView,
    setContextFilter,
  ]);

  return (
    <CanvasContext.Provider value={value}>
      {children}
    </CanvasContext.Provider>
  );
}

// Safe fallback for when useCanvas is called outside CanvasProvider
const CANVAS_NOOP = {
  widgets: [],
  addWidget: () => {},
  addWidgetsFromAI: () => {},
  updateWidget: () => {},
  removeWidget: () => {},
  moveWidget: () => {},
  clearCanvas: () => {},
  selectedWidgetId: null,
  setSelectedWidgetId: () => {},
  canvasMode: 'view',
  setCanvasMode: () => {},
};

export const useCanvas = () => {
  const context = useContext(CanvasContext);
  // Return safe noop if no provider (e.g., ChatWidget used outside canvas routes)
  if (!context) {
    return CANVAS_NOOP;
  }
  return context;
};

export default CanvasContext;
